/**
 * "Complete your kit" add-ons.
 *
 * Checked extras are posted to /cart/add.js immediately before the theme's own
 * add-to-cart runs, so they land as real line items. The click is intercepted in the
 * capture phase, the extras are added, then the original button is clicked again with
 * a flag so the theme's normal flow proceeds untouched.
 */
function checkedExtras() {
  const items = [];
  for (const input of document.querySelectorAll('.upsell-kit__checkbox:checked')) {
    const id = Number(input.dataset.upsellVariant);
    const quantity = Number(input.dataset.upsellQuantity) || 1;
    if (Number.isFinite(id) && id > 0) {
      // The discount function only takes the add-on price off lines carrying this
      // property, so the same product bought straight from a collection page pays
      // full price. The underscore hides it from the cart and checkout.
      items.push({ id, quantity, properties: { _nb_addon: '1' } });
    }
  }
  return items;
}

/**
 * Exposed so the bundle selector's direct-checkout path can include the same items.
 * That path builds one cart/add.js request and redirects, bypassing the click
 * interception below entirely - without this it silently shipped the shopper to
 * checkout without the add-ons they ticked.
 */
window.noonbrookCheckedExtras = checkedExtras;

const ADD_SELECTOR = '[ref="addToCartButton"], form[data-type="add-to-cart-form"] button[name="add"]';

document.addEventListener(
  'click',
  (event) => {
    const button = event.target.closest(ADD_SELECTOR);
    if (!button || button.dataset.upsellHandled === 'true') return;
    // The sticky bar shares ref="addToCartButton" but no longer adds to cart — it
    // scrolls back to the bundle selector, so it must not post the add-ons either.
    if (button.closest('sticky-add-to-cart')) return;

    const extras = checkedExtras();
    if (extras.length === 0) return;

    event.preventDefault();
    event.stopPropagation();

    fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: extras }),
    })
      .catch(() => {
        /* If the extras fail, still let the main product through. */
      })
      .finally(() => {
        // Uncheck so a second add-to-cart doesn't silently duplicate the extras.
        for (const input of document.querySelectorAll('.upsell-kit__checkbox:checked')) {
          input.checked = false;
        }

        button.dataset.upsellHandled = 'true';
        button.click();
        delete button.dataset.upsellHandled;
      });
  },
  true
);

/* "See N more" reveals the remaining add-ons. */
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-upsell-more]');
  if (!trigger) return;

  const root = trigger.closest('upsell-kit');
  if (!root) return;

  for (const item of root.querySelectorAll('[data-upsell-hidden]')) {
    item.hidden = false;
    item.removeAttribute('data-upsell-hidden');
  }
  trigger.remove();
});


/* ---- Quantity stepper, colour swatches, live price ---- */

function formatMoney(cents) {
  const currency = window.Shopify?.currency?.active || 'USD';
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency,
    }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

function repriceRow(row) {
  const input = row.querySelector('.upsell-kit__checkbox');
  if (!input) return;

  const unit = Number(input.dataset.unitPrice) || 0;
  const qty = Number(input.dataset.upsellQuantity) || 1;
  const discount = Number(input.dataset.discount) || 0;

  const full = unit * qty;
  const total = Math.round((full * (100 - discount)) / 100);

  const price = row.querySelector('[data-upsell-price]');
  const compare = row.querySelector('[data-upsell-compare]');
  const each = row.querySelector('[data-upsell-each]');
  const badge = row.querySelector('[data-upsell-qty-badge]');

  if (price) price.textContent = formatMoney(total);
  if (compare) compare.textContent = formatMoney(full);

  if (each) {
    each.textContent = `${formatMoney(Math.round(total / qty))} each`;
    each.hidden = qty < 2;
  }
  if (badge) {
    badge.textContent = `x${qty}`;
    badge.hidden = qty < 2;
  }
}

/**
 * Mirror the bundle tier's quantity onto every add-on: choosing "Buy 1 Get 1"
 * (qty 2) makes the extras qty 2, "Buy 2 Get 2" (qty 4) makes them 4.
 */
function syncQuantityFromBundle(quantity) {
  const qty = Math.min(10, Math.max(1, Number(quantity) || 1));

  for (const row of document.querySelectorAll('.upsell-kit__row')) {
    const input = row.querySelector('.upsell-kit__checkbox');
    if (!input) continue;

    input.dataset.upsellQuantity = String(qty);
    const label = row.querySelector('[data-upsell-qty-value]');
    if (label) label.textContent = String(qty);
    repriceRow(row);
  }
}

document.addEventListener('click', (event) => {
  const tier = event.target.closest('[data-bundle-tier]');
  if (tier) syncQuantityFromBundle(tier.getAttribute('data-quantity'));
});

/* Match whatever tier is preselected on load. */
function initFromBundle() {
  const selected =
    document.querySelector('[data-bundle-tier][data-default="true"]') ||
    document.querySelector('[data-bundle-tier]');
  if (selected) syncQuantityFromBundle(selected.getAttribute('data-quantity'));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFromBundle);
} else {
  initFromBundle();
}

document.addEventListener('click', (event) => {
  const step = event.target.closest('[data-upsell-step]');
  if (step) {
    // Inside a <label>, so stop the click from toggling the checkbox.
    event.preventDefault();
    event.stopPropagation();

    const row = step.closest('.upsell-kit__row');
    const input = row?.querySelector('.upsell-kit__checkbox');
    if (!input) return;

    const delta = Number(step.dataset.upsellStep) || 0;
    const current = Number(input.dataset.upsellQuantity) || 1;

    // Stepping below 1 removes the add-on instead of doing nothing. Shoppers reach
    // for the minus to get rid of it and previously had to find the checkbox, so
    // the row just sat there unchanged and looked broken.
    if (delta < 0 && current <= 1) {
      if (input.checked) {
        input.checked = false;
        // Bubbles to the `change` listener below, which reprices the CTA via
        // announceExtras - mirroring what unticking the box by hand would do.
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      repriceRow(row);
      return;
    }

    // Stepping up on a deselected row selects it, otherwise the quantity climbs
    // while the item is not actually in the order.
    if (delta > 0 && !input.checked) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const next = Math.min(10, Math.max(1, current + delta));
    input.dataset.upsellQuantity = String(next);

    const label = row.querySelector('[data-upsell-qty-value]');
    if (label) label.textContent = String(next);
    repriceRow(row);
    return;
  }

  const swatch = event.target.closest('[data-upsell-swatch]');
  if (swatch) {
    event.preventDefault();
    event.stopPropagation();

    const row = swatch.closest('.upsell-kit__row');
    const input = row?.querySelector('.upsell-kit__checkbox');
    if (!input) return;

    input.dataset.upsellVariant = swatch.dataset.upsellSwatch;
    const price = Number(swatch.dataset.upsellSwatchPrice);
    if (Number.isFinite(price) && price > 0) input.dataset.unitPrice = String(price);

    for (const other of row.querySelectorAll('[data-upsell-swatch]')) {
      other.classList.toggle('is-selected', other === swatch);
    }

    const name = row.querySelector('[data-upsell-variant-name]');
    if (name) name.textContent = swatch.getAttribute('title') || '';

    repriceRow(row);
  }
});


/* ---- Broadcast the checked add-on total ----
   The CTA shows bundle + extras, so it has to know when the selection changes. */
function checkedExtrasTotal() {
  let cents = 0;      // what the add-ons cost
  let compare = 0;    // their undiscounted worth, for the struck price
  for (const input of document.querySelectorAll('.upsell-kit__checkbox:checked')) {
    const unit = Number(input.dataset.unitPrice) || 0;
    const qty = Number(input.dataset.upsellQuantity) || 1;
    const discount = Number(input.dataset.discount) || 0;
    cents += Math.round((unit * qty * (100 - discount)) / 100);
    compare += unit * qty;
  }
  return { cents, compare };
}

function announceExtras() {
  document.dispatchEvent(new CustomEvent('upsell:change', { detail: checkedExtrasTotal() }));
}

document.addEventListener('change', (event) => {
  if (event.target.closest('.upsell-kit__checkbox')) announceExtras();
});

// Stepper and swatch clicks change price without firing `change` on the checkbox.
document.addEventListener('click', (event) => {
  if (event.target.closest('[data-upsell-step], [data-upsell-swatch]')) {
    requestAnimationFrame(announceExtras);
  }
});
